/**
 * Determines if the given date is a federal holiday.
 * Includes weekend observance logic (preceding Friday or following Monday).
 * @param {Date} date - The date to check
 * @returns {boolean} True if the date is a federal holiday
 */
export const isFederalHoliday = date => {
  const holidayInfo = getFederalHolidayInfo(date);
  return holidayInfo !== null;
};

/**
 * Gets federal holiday information for the given date.
 * Includes weekend observance logic (preceding Friday or following Monday).
 * Also checks if tomorrow is a holiday to provide advance notice.
 * @param {Date} date - The date to check
 * @returns {Object|null} Holiday info object with name and isAdvanceNotice properties, or null if not a holiday
 */
export const getFederalHolidayInfo = date => {
  // First check if today is a holiday
  const todayHoliday = checkHolidayForDate(date);
  if (todayHoliday) {
    return { name: todayHoliday, isAdvanceNotice: false };
  }

  // Then check if tomorrow is a holiday (advance notice)
  const tomorrow = new Date(date);
  tomorrow.setDate(date.getDate() + 1);
  const tomorrowHoliday = checkHolidayForDate(tomorrow);
  if (tomorrowHoliday) {
    return { name: tomorrowHoliday, isAdvanceNotice: true };
  }

  return null;
};

/**
 * Checks if a specific date is a federal holiday.
 * @param {Date} date - The date to check
 * @returns {string|null} Holiday name or null
 */
const checkHolidayForDate = date => {
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const weekday = date.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  const nthWeekDay = Math.ceil(day / 7);

  const isThursday = weekday === 4;
  const isFriday = weekday === 5;
  const isMonday = weekday === 1;
  const isWeekend = weekday === 0 || weekday === 6;

  // New Year's Day (Jan 1, or preceding Friday/following Monday if weekend)
  if (
    (month === 12 && day === 31 && isFriday) ||
    (month === 1 && day === 1 && !isWeekend) ||
    (month === 1 && day === 2 && isMonday)
  ) {
    return "New Year's Day";
  }

  // MLK Day (3rd Monday in January)
  if (month === 1 && isMonday && nthWeekDay === 3) {
    return 'Martin Luther King Jr. Day';
  }

  // President's Day (3rd Monday in February)
  if (month === 2 && isMonday && nthWeekDay === 3) {
    return "Presidents' Day";
  }

  // Memorial Day (Last Monday in May)
  const nextWeek = new Date(date);
  nextWeek.setDate(day + 7);
  if (month === 5 && isMonday && nextWeek.getMonth() + 1 === 6) {
    return 'Memorial Day';
  }

  // Juneteenth (June 19, or preceding Friday/following Monday if weekend)
  if (
    (month === 6 && day === 18 && isFriday) ||
    (month === 6 && day === 19 && !isWeekend) ||
    (month === 6 && day === 20 && isMonday)
  ) {
    return 'Juneteenth';
  }

  // Independence Day (July 4, or preceding Friday/following Monday if weekend)
  if (
    (month === 7 && day === 3 && isFriday) ||
    (month === 7 && day === 4 && !isWeekend) ||
    (month === 7 && day === 5 && isMonday)
  ) {
    return 'Independence Day';
  }

  // Labor Day (1st Monday in September)
  if (month === 9 && isMonday && nthWeekDay === 1) {
    return 'Labor Day';
  }

  // Columbus Day (2nd Monday in October)
  if (month === 10 && isMonday && nthWeekDay === 2) {
    return 'Columbus Day';
  }

  // Veterans Day (November 11, or preceding Friday/following Monday if weekend)
  if (
    (month === 11 && day === 10 && isFriday) ||
    (month === 11 && day === 11 && !isWeekend) ||
    (month === 11 && day === 12 && isMonday)
  ) {
    return 'Veterans Day';
  }

  // Thanksgiving Day (4th Thursday in November)
  if (month === 11 && isThursday && nthWeekDay === 4) {
    return 'Thanksgiving Day';
  }

  // Christmas Day (December 25, or preceding Friday/following Monday if weekend)
  if (
    (month === 12 && day === 24 && isFriday) ||
    (month === 12 && day === 25 && !isWeekend) ||
    (month === 12 && day === 26 && isMonday)
  ) {
    return 'Christmas Day';
  }

  return null;
};
